export default ({ Schema, PointSchema, FormSchema }) => {
  const AllowanceClientSchema = new Schema(
    {
      code: { type: Number, required: true },
      name: { type: String, default: '' },
      startDate: { type: Date, required: true },
      reason: { type: String, default: '' },
    },
    { _id: false }
  )

  const CoachingParametersSchema = new Schema({
    jobTitle: { type: String, required: true },
    pdvMinDistance: { type: Number, default: 0 },
    outRouteMultiplier: { type: Number, default: 0.2 },
  })

  const LocationSchema = new Schema({
    distance: { type: Number, default: null },
    coordinates: { type: PointSchema, default: null },
    approved: { type: Boolean },
    hasInvalidCoordinates: { type: Boolean, default: false },
    inRange: { type: Boolean, default: true },
    approval: {
      type: String,
      default: 'auto',
      enum: ['auto', 'manual', 'waiting'],
    },
  })

  const DeviceSchema = new Schema({
    time: String,
    battery: String,
    network: String,
  })

  const CheckSchema = new Schema({
    timestamp: { type: Date, default: () => new Date() },
    timezone: { type: Number, default: () => new Date().getTimezoneOffset() },
    location: { type: LocationSchema, default: null },
    device: { type: DeviceSchema },
  })

  const PDVPotential = new Schema(
    {
      bovine: { type: Number, default: null },
      thirdParty: { type: Number, default: null },
      pva: { type: Number, default: null },
    },
    { _id: false }
  )

  const PDVSchema = new Schema({
    route: { type: mongoose.Schema.Types.ObjectId, ref: 'Routes' },
    clientCode: { type: Number, required: true },
    activeAllowance: { type: 'Mixed', default: null },
    name: { type: String, required: true },
    inRoute: { type: Boolean, default: true },
    invalidated: { type: Boolean, default: false },
    status: {
      type: String,
      required: true,
      enum: ['being', 'absent', 'inactive', 'overdue', 'discard'],
    },
    location: { type: PointSchema, default: null },
    coachingObservation: { type: String, default: '' },
    potential: {
      type: PDVPotential,
      default: () => ({}),
    },
  })

  const StageSchema = new Schema({
    stage: {
      type: String,
      required: true,
      enum: ['pre-process', 'process', 'post-process'],
    },
    pdv: { type: PDVSchema, required: false },
    form: { type: FormSchema },
    checkIn: { type: CheckSchema, required: true },
    checkOut: { type: CheckSchema },
    completed: { type: Boolean, default: false },
    discarded: { type: Boolean, default: false },
    score: {
      answered: { type: Number, default: 0 },
      total: { type: Number, default: 0 },
      grade: { type: Number, default: 0 },
      disqualifyingAnswerGiven: { type: Boolean, default: false }, // Quando verdadeiro deve zerar a nota do coaching porém mantendo a nota da visita para controle
    },
  })

  const EmployeeReferenceSchema = new Schema(
    {
      employeeCode: { type: Number },
      teleSales: { type: Boolean, default: () => false },
      name: { type: String, required: true },
      jobTitle: { type: String, required: true },
    },
    { _id: false }
  )

  const DiagnosisFeedbackItem = new Schema(
    {
      description: { type: String, required: true },
      value: { type: Number, required: true },
    },
    { _id: false }
  )

  const FeedbackSchema = new Schema(
    {
      strength: { type: String, default: null },
      opportunity: { type: String, default: null },
      commitment: { type: String, default: null },
      diagnosis: { type: [DiagnosisFeedbackItem], default: () => [] },
      development: { type: [DiagnosisFeedbackItem], default: () => [] },
    },
    { _id: false }
  )

  const CoachingSchema = new Schema(
    {
      // Pessoas envolvidas no coaching
      coach: { type: EmployeeReferenceSchema, required: true },
      coachee: { type: EmployeeReferenceSchema, required: true },
      salesman: { type: EmployeeReferenceSchema, default: () => null },
      generic: { type: Boolean, default: () => false },
      teleSales: { type: Boolean, default: () => false },
      // Dados da rota
      routes: {
        hash: String,
        in: [Object],
        out: [Object],
      },
      forms: { type: [FormSchema], default: () => [] },
      activeAllowanceClients: {
        type: [AllowanceClientSchema],
        default: () => [],
      },
      visitedAllowanceClients: { type: [Number], default: () => [] },
      weekday: { type: Number, min: 0, max: 7, required: true },
      // Meta dados
      coachingStage: {
        type: String,
        default: 'pre-process',
        enum: [
          'pre-process',
          'process',
          'post-process',
          'feedback',
          'done',
          'discarded',
        ],
      },
      expired: { type: Boolean, default: false },
      changeHistory: {
        entries: {
          type: [Date],
          default: () => [],
        },
        inconsistent: {
          type: Boolean,
          default: () => false,
        },
      },
      // Configurações
      parameters: { type: CoachingParametersSchema, required: true },
      // Dados para relatórios
      date: { type: Date, required: true },
      weekDate: {
        year: { type: Number, required: true },
        week: { type: Number, required: true },
        day: { type: Number, required: true },
      },
      feedback: {
        type: String,
        default: '',
      },
      feedbacks: {
        type: FeedbackSchema,
        default: () => ({}),
      },
      score: {
        pdvs: { type: Number, default: 0 },
        total: { type: Number, default: 0 },
        grade: { type: Number, default: 0 },
      },
      statistics: {
        goals: {
          minInRoute: { type: Number, required: true },
          maxOutRoute: { type: Number, required: true },
          minRoutesAchivied: { type: Boolean, default: false },
        },
        pdvs: {
          planned: { type: Number, required: true },
          visited: { type: Number, default: 0 },
          inRoute: { type: Number, default: 0 },
          outRoute: { type: Number, default: 0 },
          invalid: { type: Number, default: 0 },
        },
      },
      // Estágios do coaching
      stages: {
        type: [StageSchema],
        default: () => [],
      },
      searchKeys: {
        type: [String],
        default: () => [],
      },
      scoreAlgorithmVersion: {
        type: String,
        default: 'v2.0',
      },
      appVersion: {
        type: [String],
        default: () => [window.$versionTag],
      },
    },
    { timestamps: true }
  )

  return CoachingSchema
}
